import { useState } from "react";
import clsx from "clsx";
import { useMediaObjectUrl } from "../../app/hooks/useMedia.js";
import { releaseGradient } from "./releaseFormat.js";
import styles from "./MediaImage.module.scss";

// A media image slot. Renders the entity payload's CDN `url` when present (go-live, with sized
// variants + srcset owned by the backend), else the authenticated `/raw` bytes resolved by `mediaId`
// (the dev degrade — the payload url is null there). The frame ALWAYS reserves aspect-ratio (no layout shift)
// and falls back to the deterministic release gradient while loading or on error, so a missing/failed cover is
// never a broken image (born-blank). loading="lazy" for below-the-fold slots. Variant/srcset generation is
// backend-owned; a single URL renders now, the srcset lands with the pipeline.

export interface MediaImageProps {
  /** The media id to resolve (degrade: fetch `/raw`). Null → the gradient placeholder. */
  mediaId?: string | null;
  /** A server-provided CDN url — used verbatim when present (go-live). */
  url?: string | null;
  /** Seeds the fallback gradient (usually the release id) so an empty slot is stable, not random. */
  seed?: string | null;
  /** Alt text; pass "" for a purely decorative cover (paired with a text label elsewhere). */
  alt: string;
  className?: string;
  /** Defer loading for below-the-fold slots (photos gallery, tiles). Above-the-fold hero passes false. */
  lazy?: boolean;
}

export function MediaImage({ mediaId, url, seed, alt, className, lazy = true }: MediaImageProps) {
  const media = useMediaObjectUrl(mediaId, url);
  const [loaded, setLoaded] = useState(false);
  const hasImage = Boolean(media.url) && !media.error;

  return (
    <div
      className={clsx(styles.frame, className)}
      style={{ background: releaseGradient(seed) }}
      data-loading={media.loading || undefined}
    >
      {hasImage && (
        <img
          className={styles.img}
          src={media.url}
          alt={alt}
          loading={lazy ? "lazy" : undefined}
          data-loaded={loaded || undefined}
          onLoad={() => setLoaded(true)}
        />
      )}
    </div>
  );
}
